import { useMemo, useState } from "react";
import { Trophy } from "lucide-react";
import { eur0, isWon, monthLabel, netOf, NO_OWNER, ownerKey } from "./pipelineUtils";

const TOP = 5;
const MEDAL = ["text-amber-300", "text-zinc-300", "text-orange-400"];

function rankReps(projects) {
  const m = new Map();
  projects.forEach((p) => {
    const id = ownerKey(p);
    if (id === NO_OWNER) return;
    const r = m.get(id) || { id, name: p.owner_name, won: 0, wonCount: 0, open: 0 };
    if (isWon(p)) { r.won += netOf(p); r.wonCount += 1; } else { r.open += netOf(p); }
    m.set(id, r);
  });
  return [...m.values()].sort((a, b) => b.won - a.won || b.open - a.open || a.name.localeCompare(b.name, "de"));
}

// Rangliste der Vertriebler nach gewonnenem Netto-Umsatz (folgt der Monatsauswahl).
export function RepRanking({ projects, month }) {
  const [all, setAll] = useState(false);
  const reps = useMemo(() => rankReps(projects || []), [projects]);
  const max = Math.max(...reps.map((r) => r.won), 1);
  const shown = all ? reps : reps.slice(0, TOP);
  return (
    <div className="mt-4 bg-[#18181B] border border-zinc-800 rounded-2xl p-4" data-testid="rep-ranking">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="inline-flex items-center gap-2">
          <Trophy size={15} className="text-amber-300" />
          <h3 className="text-[12px] uppercase tracking-widest font-bold text-zinc-200">Vertriebler-Rangliste</h3>
        </div>
        <span className="text-[11px] text-zinc-500" data-testid="rep-ranking-period">
          {month ? monthLabel(month) : "Alle Zeiträume"} · gewonnen netto
        </span>
      </div>
      {!reps.length && (
        <div className="py-4 text-center text-[12px] text-zinc-500" data-testid="rep-ranking-empty">Keine Umsätze in diesem Zeitraum.</div>
      )}
      <ol className="space-y-1.5">
        {shown.map((r, i) => (
          <li key={r.id} className="grid grid-cols-[22px_1fr_auto] items-center gap-3" data-testid={`rep-ranking-row-${r.id}`}>
            <span className={`text-[12px] font-bold font-mono text-right ${MEDAL[i] || "text-zinc-500"}`}>{i + 1}.</span>
            <div className="min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[13px] font-semibold truncate">{r.name}</span>
                <span className="text-[11px] text-zinc-500 shrink-0">{r.wonCount} gewonnen · offen {eur0(r.open)}</span>
              </div>
              <div className="h-1.5 mt-1 rounded-full bg-zinc-800 overflow-hidden">
                <div className="h-full rounded-full bg-[#00E575] transition-[width] duration-500" style={{ width: `${(r.won / max) * 100}%` }} />
              </div>
            </div>
            <span className="text-[13px] font-mono font-bold text-[#00E575] w-[96px] text-right" data-testid={`rep-ranking-won-${r.id}`}>{eur0(r.won)}</span>
          </li>
        ))}
      </ol>
      {reps.length > TOP && (
        <button type="button" onClick={() => setAll((v) => !v)} data-testid="rep-ranking-toggle"
          className="mt-3 text-[12px] text-zinc-400 hover:text-[#00E575]">
          {all ? "Weniger zeigen" : `Alle ${reps.length} Vertriebler anzeigen`}
        </button>
      )}
    </div>
  );
}
